import React, { useMemo, useState } from "react"
import { Box, Paper, Stack, Typography } from "@mui/material"
import SideBarLayout from "../../../layout/side-bar-layout"
import { TITLE_DESIGN_SYSTEM_PAGE } from "../../../../utils/constants"
import CodeViewer from "../../../components/CodeViewer/code-viewer"
import GridFormSheet from "../../../components/GridFormSheet"

const GridFormSheetDesignSystemPage = () => {
    const TITLE_PAGE = `${TITLE_DESIGN_SYSTEM_PAGE} | Grid Form Sheet`

    const columnsOrder = useMemo(() => ["nome", "documento", "cidade"], [])

    const columnsConfig = useMemo(
        () => [
            { field: "nome", headerName: "Nome", flex: 1 },
            { field: "documento", headerName: "CPF/CNPJ", flex: 1 },
            { field: "cidade", headerName: "Cidade", flex: 1 },
        ],
        []
    )

    const [rows, setRows] = useState([
        { id: "row-1", nome: "Fulano da Silva", documento: "123.456.789-00", cidade: "São Paulo" },
        { id: "row-2", nome: "Ciclano Souza", documento: "98.765.432/0001-00", cidade: "Rio de Janeiro" },
    ])

    const codeUsage = `import GridFormSheet from "src/v2/components/GridFormSheet"

const columnsOrder = ["nome", "documento", "cidade"]
const columnsConfig = [
  { field: "nome", headerName: "Nome" },
  { field: "documento", headerName: "CPF/CNPJ" },
  { field: "cidade", headerName: "Cidade" },
]

const [rows, setRows] = useState([
  { id: "row-1", nome: "Fulano da Silva", documento: "123.456.789-00", cidade: "São Paulo" },
])

<GridFormSheet
  rows={rows}
  onChange={setRows}
  columnsOrder={columnsOrder}
  columnsConfig={columnsConfig}
/>`

    const codeProps = `type GridFormSheetProps = {
  rows: Array<Record<string, any>>                      // dados exibidos
  onChange: (nextRows: any[] | ((prev: any[]) => any[])) => void
  columnsOrder: string[]                                // ordem das colunas usada no paste/tab
  columnsConfig: {
    field: string
    headerName?: string
    flex?: number
    width?: number
    editable?: boolean
  }[]
}`

    return (
        <SideBarLayout
            header
            title={TITLE_PAGE}
            headerTitle={TITLE_PAGE}
        >
            <Box sx={{ mt: -2 }}>
                <Stack spacing={3}>
                    <Paper>
                        <Box p={3}>
                            <Typography variant="h4" gutterBottom color="common.white">
                                {`GridFormSheet`}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {`Planilha editável baseada no DataGrid do MUI, usada no Cadastro Manual para inserir listas (ex.: garantias). Suporta colar conteúdo do clipboard (CTRL+V), adicionar/remover linhas e navegação via Tab.`}
                            </Typography>

                            <Box mt={3}>
                                <Typography variant="h6" gutterBottom color="common.white">
                                    {`Exemplo principal`}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" paragraph>
                                    {`Experimente colar dados tabulados (Excel/Sheets) ou usar o botão ADICIONAR LINHA. O componente garante pelo menos uma linha em branco.`}
                                </Typography>

                                <Box sx={{ backgroundColor: "#14192b", p: 2, borderRadius: 1 }}>
                                    <GridFormSheet
                                        rows={rows}
                                        onChange={setRows}
                                        columnsOrder={columnsOrder}
                                        columnsConfig={columnsConfig}
                                    />
                                </Box>

                                <Box mt={2}>
                                    <Typography variant="body2" color="text.secondary">
                                        <strong>Estado atual:</strong> {JSON.stringify(rows, null, 0)}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Paper>

                    <Paper>
                        <Box p={3}>
                            <Typography variant="h6" gutterBottom color="common.white">
                                {`Uso básico`}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                {`Configuração mínima com colunas e estado controlado.`}
                            </Typography>

                            <Paper variant="outlined">
                                <Box p={2}>
                                    <CodeViewer code={codeUsage} language="jsx" />
                                </Box>
                            </Paper>
                        </Box>
                    </Paper>

                    <Paper>
                        <Box p={3}>
                            <Typography variant="h6" gutterBottom color="common.white">
                                {`Propriedades principais`}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                {`API simplificada do GridFormSheet.`}
                            </Typography>

                            <Paper variant="outlined">
                                <Box p={2}>
                                    <Typography variant="subtitle2" gutterBottom color="common.white">
                                        {`Tipo das props (exemplo em TypeScript`}
                                        {`)`}
                                    </Typography>
                                    <CodeViewer code={codeProps} language="tsx" />
                                </Box>
                            </Paper>
                        </Box>
                    </Paper>
                </Stack>
            </Box>
        </SideBarLayout>
    )
}

export default GridFormSheetDesignSystemPage
